import { useEffect, useState } from "react";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { CreditCard, Check, AlertTriangle } from "lucide-react";

const STATUS_LABELS = { trial: "Testphase", aktiv: "Aktiv", zahlung_ausstehend: "Zahlung ausstehend", gesperrt: "Gesperrt", gekuendigt: "Gekündigt" };

export default function Abrechnung() {
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/billing").then((r) => setBilling(r.data));
    api.get("/public/plans").then((r) => setPlans(r.data));
  };
  useEffect(load, []);

  const subscribe = async (plan) => {
    setBusy(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Checkout fehlgeschlagen"));
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!window.confirm("Abonnement wirklich kündigen?")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Abonnement gekündigt.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!billing) return <div className="text-sm font-mono text-zinc-400">Laden…</div>;
  const { tenant, plan, transactions } = billing;

  return (
    <div className="max-w-4xl space-y-6" data-testid="abrechnung-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Abo & Abrechnung</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Ihr Abonnement</h1>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm flex flex-wrap justify-between items-center gap-4" data-testid="subscription-status-card">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Aktueller Tarif</div>
          <div className="font-display text-2xl font-semibold mt-1">{plan?.name || "–"} {plan?.price_monthly != null && <span className="text-base font-normal text-zinc-500">· {eur(plan.price_monthly)}/Monat</span>}</div>
          <div className="text-sm text-zinc-500 mt-1">
            Status: <b>{STATUS_LABELS[tenant.status]}</b>
            {tenant.status === "trial" && ` · Testphase bis ${tenant.trial_ends}`}
          </div>
          {plan?.limits && (
            <div className="text-xs font-mono text-zinc-400 mt-2">
              Limits: {plan.limits.users === -1 ? "∞" : plan.limits.users} Benutzer · {plan.limits.ki_imports === -1 ? "∞" : plan.limits.ki_imports} KI-Imports/Monat · {plan.limits.storage_mb === -1 ? "∞" : `${plan.limits.storage_mb / 1024} GB`} Speicher
            </div>
          )}
        </div>
        {tenant.status !== "gekuendigt" && tenant.status !== "trial" && (
          <Button variant="outline" onClick={cancel} data-testid="cancel-subscription-button" className="text-red-600 border-red-200 hover:bg-red-50">Kündigen</Button>
        )}
      </div>

      {tenant.status === "zahlung_ausstehend" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-testid="payment-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="text-sm text-amber-900">Die letzte Zahlung ist fehlgeschlagen. Bitte Zahlungsmethode erneut hinterlegen, um Einschränkungen zu vermeiden.</div>
        </div>
      )}

      <div>
        <h2 className="font-display font-medium text-lg mb-4">Tarife</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.key} className={`rounded-xl border p-5 ${p.key === tenant.plan_key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200"}`} data-testid={`billing-plan-${p.key}`}>
              <div className="font-mono text-xs uppercase tracking-wider opacity-60">{p.name}</div>
              <div className="font-display text-xl font-semibold mt-2">{p.price_monthly != null ? `${eur(p.price_monthly)}/Monat` : "Individuell"}</div>
              <ul className="mt-3 space-y-1 text-xs opacity-80">
                {p.features?.slice(0, 4).map((f) => <li key={f} className="flex gap-1.5"><Check className="w-3 h-3 mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              {p.key === tenant.plan_key && tenant.status !== "trial" ? (
                <div className="mt-4 text-xs font-mono opacity-70">Ihr aktueller Tarif</div>
              ) : p.enterprise ? (
                <div className="mt-4 text-xs font-mono opacity-70">Auf Anfrage</div>
              ) : (
                <Button onClick={() => subscribe(p)} disabled={busy === p.key} data-testid={`billing-subscribe-${p.key}`}
                  className={`mt-4 w-full ${p.key === tenant.plan_key ? "bg-white text-zinc-900 hover:bg-zinc-200" : "bg-zinc-900 hover:bg-zinc-800 text-white"}`}>
                  <CreditCard className="w-4 h-4 mr-1" /> {busy === p.key ? "Weiterleitung…" : p.key === tenant.plan_key ? "Zahlung einrichten" : "Tarif wählen"}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100"><h3 className="font-display font-medium">Zahlungen</h3></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Datum</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Tarif</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Status</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Betrag</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {transactions.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-zinc-400">Noch keine Zahlungen.</td></tr>}
            {transactions.map((t) => (
              <tr key={t.session_id} data-testid={`transaction-row-${t.session_id.slice(-6)}`}>
                <td className="px-5 py-3 text-zinc-600">{deDate(t.created_at)}</td>
                <td className="px-5 py-3 font-mono text-xs">{t.lookup_key}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-600"}`}>
                    {t.payment_status === "paid" ? "Bezahlt" : t.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-right font-medium">{eur(t.amount / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
